import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Copy,
  CreditCard,
  Image as ImageIcon,
  ImagePlus,
  ListChecks,
  Pencil,
  Plus,
  ShoppingCart,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type {
  CreatePurchaseIntentResponse,
  EquipmentCategory,
  EquipmentItemImageResponse,
  EquipmentItemResponse,
  EquipmentItemSize,
  EquipmentPaypalSettingsResponse,
  EquipmentPurchaseResponse,
} from '@bm/contracts';
import {
  EQUIPMENT_CATEGORIES,
  EQUIPMENT_CATEGORY_LABELS,
  SELLABLE_EQUIPMENT_CATEGORIES,
} from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { fileToBase64 } from '../lib/file-upload';
import { useClickOutside } from '../lib/use-click-outside';
import { useLockBodyScroll } from '../lib/use-lock-body-scroll';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { Modal } from '../components/Modal';

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

interface FormState {
  category: EquipmentCategory;
  name: string;
  description: string;
  sizes: EquipmentItemSize[];
  price: string;
}

const EMPTY_FORM: FormState = {
  category: 'racket',
  name: '',
  description: '',
  sizes: [],
  price: '',
};

/** Cent-Betrag als Eingabewert für das Preisfeld ("12,50"). */
function centsToPriceInput(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

/** Preis-Eingabe ("12,50" oder "12.50") in Cent, oder `null` bei ungültiger Eingabe. */
function parsePriceInput(value: string): number | null {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return null;
  const amount = Number(normalized);
  if (!Number.isFinite(amount) || amount < 0) return null;
  return Math.round(amount * 100);
}

function formatPrice(cents: number): string {
  return `${(cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

/** Leere Zeilen (nie ausgefüllte Größe) fliegen raus, der Rest wird
 * getrimmt — so entstehen beim Absenden keine leeren `size: ""`-Einträge
 * durch übrig gebliebene, nie befüllte Zeilen aus "Größe hinzufügen". */
function cleanSizes(sizes: EquipmentItemSize[]): EquipmentItemSize[] {
  return sizes
    .map((s) => ({ size: s.size.trim(), quantity: s.quantity }))
    .filter((s) => s.size.length > 0);
}

/** Größe + Bestandsmenge, editierbar als Zeilenliste im Anlegen-/
 * Bearbeiten-Dialog — "Größe" bleibt Freitext (Griffstärke, EU-Größe, S/M/L,
 * je nach Rubrik unterschiedlich), nur die Menge ist ein Zahlenfeld. */
function SizesField({
  sizes,
  onChange,
}: {
  sizes: EquipmentItemSize[];
  onChange: (sizes: EquipmentItemSize[]) => void;
}) {
  return (
    <Field label="Größen & Bestand (optional)">
      <div className="space-y-2">
        {sizes.map((row, i) => (
          <div key={i} className="flex items-center gap-2">
            {/* Breite über einen Wrapper statt einer Klasse direkt auf
             * <Input>: die Komponente bringt bereits `w-full` fest mit — eine
             * zusätzliche Breiten-Klasse (`flex-1`/`w-24`) direkt am Input
             * konkurriert damit unvorhersehbar (Tailwinds Klassenreihenfolge
             * in der generierten CSS-Datei entscheidet, nicht die Reihenfolge
             * im className-String), ein Wrapper-Div umgeht das sauber. */}
            <div className="flex-1">
              <Input
                placeholder="Größe, z. B. M"
                value={row.size}
                onChange={(e) =>
                  onChange(sizes.map((r, j) => (j === i ? { ...r, size: e.target.value } : r)))
                }
              />
            </div>
            <div className="w-24">
              <Input
                type="number"
                min={0}
                step={1}
                placeholder="Menge"
                value={row.quantity}
                onChange={(e) =>
                  onChange(
                    sizes.map((r, j) =>
                      j === i ? { ...r, quantity: Math.max(0, Number(e.target.value) || 0) } : r,
                    ),
                  )
                }
              />
            </div>
            <button
              type="button"
              onClick={() => onChange(sizes.filter((_, j) => j !== i))}
              className="shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
              aria-label="Größe entfernen"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange([...sizes, { size: '', quantity: 0 }])}
          className="flex items-center gap-1 text-xs font-medium text-brand-accent hover:underline"
        >
          <Plus size={12} /> Größe hinzufügen
        </button>
      </div>
    </Field>
  );
}

function CategoryTabs({
  active,
  onChange,
  counts,
}: {
  active: EquipmentCategory;
  onChange: (c: EquipmentCategory) => void;
  counts: Record<EquipmentCategory, number>;
}) {
  return (
    <div className="inline-flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-slate-100 p-1">
      {EQUIPMENT_CATEGORIES.map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={`flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors ${
            active === c
              ? 'bg-white text-slate-900 shadow-sm'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {EQUIPMENT_CATEGORY_LABELS[c]}
          <span
            className={`rounded-full px-1.5 text-xs ${
              active === c ? 'bg-slate-100 text-slate-500' : 'bg-slate-200 text-slate-400'
            }`}
          >
            {counts[c]}
          </span>
        </button>
      ))}
    </div>
  );
}

function ItemDialog({
  initial,
  onClose,
  onSubmit,
  submitting,
  error,
}: {
  initial: FormState;
  onClose: () => void;
  onSubmit: (form: FormState) => void;
  submitting: boolean;
  error: string | null;
}) {
  const [form, setForm] = useState<FormState>(initial);
  const priceCents = parsePriceInput(form.price);
  return (
    <Modal
      title={initial.name ? 'Produkt bearbeiten' : 'Produkt anlegen'}
      icon={<ShoppingCart size={18} />}
      onClose={onClose}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(form);
        }}
      >
        {error && <Alert>{error}</Alert>}
        <Field label="Rubrik">
          <select
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
            value={form.category}
            onChange={(e) =>
              setForm((f) => ({ ...f, category: e.target.value as EquipmentCategory }))
            }
          >
            {EQUIPMENT_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {EQUIPMENT_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Name">
          <Input
            required
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="z. B. Yonex Astrox 99 Pro"
          />
        </Field>
        <Field label="Beschreibung">
          <textarea
            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
            rows={4}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Eigenschaften, Zustand, Testbedingungen …"
          />
        </Field>
        <SizesField sizes={form.sizes} onChange={(sizes) => setForm((f) => ({ ...f, sizes }))} />
        <Field label="Preis">
          <div className="relative">
            <Input
              required
              inputMode="decimal"
              value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              placeholder="0,00"
              className="pr-8"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">
              €
            </span>
          </div>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={!form.name.trim() || priceCents === null || submitting}>
            Speichern
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Vollbild-Ansicht aller Produktbilder — Pfeile/Punkte nur, wenn mehr als
 * eins hochgeladen wurde; bei genau einem Bild eine reine Vergrößerung. */
function Lightbox({
  images,
  initialIndex,
  title,
  onClose,
}: {
  images: EquipmentItemImageResponse[];
  initialIndex: number;
  title: string;
  onClose: () => void;
}) {
  const [index, setIndex] = useState(initialIndex);
  const image = images[index];
  useLockBodyScroll();
  if (!image) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div className="relative max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/80 hover:text-white"
          aria-label="Schließen"
        >
          <X size={22} />
        </button>
        <img
          src={`/api/org/files/${image.fileId}`}
          alt={title}
          className="max-h-[75vh] max-w-full rounded-lg object-contain"
        />
        {images.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => setIndex((i) => (i - 1 + images.length) % images.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white hover:bg-black/60"
              aria-label="Vorheriges Bild"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              onClick={() => setIndex((i) => (i + 1) % images.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white hover:bg-black/60"
              aria-label="Nächstes Bild"
            >
              <ChevronRight size={20} />
            </button>
            <div className="mt-3 flex justify-center gap-1.5">
              {images.map((img, i) => (
                <button
                  key={img.id}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`Bild ${i + 1}`}
                  className={`h-1.5 w-1.5 rounded-full ${i === index ? 'bg-white' : 'bg-white/40'}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Verwaltung der Produktbilder (hochladen/löschen/Titelbild wählen) — nur
 * für `equipment.manage`. Lösch- und Titelbild-Buttons sind bewusst immer
 * sichtbar statt nur bei Hover — auf Touch-Geräten gibt es keinen Hover-
 * Zustand, ein rein hover-abhängiger Button wäre dort gar nicht erreichbar. */
function ManageImagesDialog({
  item,
  onClose,
}: {
  item: EquipmentItemResponse;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_IMAGE_BYTES) throw new Error('Bild darf höchstens 2 MB groß sein.');
      const contentBase64 = await fileToBase64(file);
      return api(`/equipment/${item.id}/images`, {
        method: 'POST',
        orgScoped: true,
        body: { filename: file.name, mimeType: file.type, contentBase64 },
      });
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['equipment'] });
    },
    onError: (e) =>
      setError(
        e instanceof ApiError || e instanceof Error ? e.message : 'Hochladen fehlgeschlagen.',
      ),
  });

  const remove = useMutation({
    mutationFn: (imageId: string) =>
      api(`/equipment/${item.id}/images/${imageId}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['equipment'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Löschen fehlgeschlagen.'),
  });

  const setCover = useMutation({
    mutationFn: (imageId: string) =>
      api(`/equipment/${item.id}/images/${imageId}/cover`, { method: 'POST', orgScoped: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['equipment'] }),
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : 'Titelbild konnte nicht gesetzt werden.'),
  });

  return (
    <Modal title={`Bilder — ${item.name}`} icon={<ImagePlus size={18} />} onClose={onClose}>
      <div className="space-y-4">
        {error && <Alert>{error}</Alert>}
        {item.images.length > 0 ? (
          <div className="grid grid-cols-3 gap-3">
            {item.images.map((img) => {
              const isCover = img.id === item.coverImageId;
              return (
                <div key={img.id} className="relative">
                  <img
                    src={`/api/org/files/${img.fileId}`}
                    alt={item.name}
                    className={`aspect-square w-full rounded-md border object-cover ${
                      isCover ? 'border-2 border-brand-accent' : 'border-slate-200'
                    }`}
                  />
                  {isCover && (
                    <span className="absolute left-1 top-1 rounded bg-brand-accent px-1.5 py-0.5 text-[10px] font-medium text-white">
                      Titelbild
                    </span>
                  )}
                  <div className="absolute right-1 top-1 flex gap-1">
                    {!isCover && (
                      <button
                        type="button"
                        disabled={setCover.isPending}
                        onClick={() => setCover.mutate(img.id)}
                        className="rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
                        aria-label="Als Titelbild festlegen"
                        title="Als Titelbild festlegen"
                      >
                        <Star size={12} />
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (confirm('Bild wirklich löschen?')) remove.mutate(img.id);
                      }}
                      className="rounded-full bg-black/60 p-1 text-white hover:bg-black/80"
                      aria-label="Bild löschen"
                      title="Bild löschen"
                    >
                      <X size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500">Noch keine Bilder hochgeladen.</p>
        )}
        <div>
          <input
            type="file"
            className="block w-full min-w-0 text-sm"
            accept="image/png,image/jpeg,image/webp"
            disabled={upload.isPending}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
              e.target.value = '';
            }}
          />
          <p className="mt-1 text-xs text-slate-400">
            PNG, JPEG oder WebP · max. 2 MB. Das Titelbild (Stern-Symbol) erscheint in der Liste —
            ohne eigene Auswahl ist das automatisch das älteste hochgeladene Bild.
          </p>
        </div>
        <div className="flex justify-end pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Schließen
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** PayPal-Kontoeinstellungen für den Bälle-Verkauf (§ Nutzeranfrage) — ein
 * Konto pro Verein, nicht pro Produkt, siehe docs/equipment.md. Nur bei
 * `canManage` und nur auf der "Bälle"-Rubrik sichtbar. Der IPN-Listener-Pfad
 * wird erst nach dem ersten Speichern angezeigt (ein Token entsteht erst
 * dann) — mit Kopieren-Button, da der Admin ihn als vollständige URL in
 * seinen eigenen PayPal-Kontoeinstellungen hinterlegen muss. */
function PaypalSettingsCard() {
  const qc = useQueryClient();
  const [handle, setHandle] = useState('');
  const [email, setEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: settings } = useQuery({
    queryKey: ['equipment', 'paypal-settings'],
    queryFn: () =>
      api<EquipmentPaypalSettingsResponse>('/equipment/paypal-settings', { orgScoped: true }),
  });

  useEffect(() => {
    if (!settings) return;
    setHandle(settings.paypalMeHandle);
    setEmail(settings.paypalEmail);
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      api<EquipmentPaypalSettingsResponse>('/equipment/paypal-settings', {
        method: 'POST',
        orgScoped: true,
        body: { paypalMeHandle: handle.trim(), paypalEmail: email.trim() },
      }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['equipment', 'paypal-settings'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.'),
  });

  const regenerate = useMutation({
    mutationFn: () =>
      api<EquipmentPaypalSettingsResponse>('/equipment/paypal-settings/regenerate-token', {
        method: 'POST',
        orgScoped: true,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['equipment', 'paypal-settings'] }),
    onError: (e) =>
      setError(e instanceof ApiError ? e.message : 'Token konnte nicht erneuert werden.'),
  });

  const ipnUrl = settings?.ipnListenerPath
    ? `${window.location.origin}${settings.ipnListenerPath}`
    : null;

  return (
    <Card title="PayPal-Konto für den Bälle-Verkauf">
      <p className="mb-3 text-sm text-slate-500">
        Ein PayPal.me-Konto für den ganzen Verein — der Preis je Produkt reicht, um daraus
        automatisch den passenden Zahlungs-Link zu bauen. Ob eine Zahlung wirklich abgeschlossen
        wurde, erfährt die App erst über die PayPal-Benachrichtigung unten.
      </p>
      {error && <Alert>{error}</Alert>}
      <form
        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="PayPal.me-Handle">
          <Input
            required
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
            placeholder="z. B. SCStToenis"
          />
        </Field>
        <Field label="PayPal-E-Mail-Adresse">
          <Input
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="kasse@verein.de"
          />
        </Field>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={save.isPending || !handle.trim() || !email.trim()}>
            Speichern
          </Button>
        </div>
      </form>

      {ipnUrl && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-sm font-medium text-slate-700">
            Letzter Schritt in deinem PayPal-Konto
          </p>
          <p className="mt-1 text-xs text-slate-500">
            Trage diese Adresse unter „Profil → Zahlungseinstellungen → Instant Payment
            Notifications (IPN)" als Benachrichtigungs-URL ein und aktiviere IPN — erst dadurch
            erfährt die App, wenn eine Zahlung wirklich abgeschlossen wurde.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-600">
              {ipnUrl}
            </code>
            <Button
              type="button"
              variant="ghost"
              className="shrink-0 gap-1.5 px-2 py-1 text-xs"
              onClick={() => {
                void navigator.clipboard.writeText(ipnUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? 'Kopiert' : 'Kopieren'}
            </Button>
          </div>
          <Button
            type="button"
            variant="ghost"
            className="mt-2 px-2 py-1 text-xs text-slate-500"
            disabled={regenerate.isPending}
            onClick={() => {
              if (
                confirm(
                  'Neuen Token erzeugen? Die bisherige IPN-URL funktioniert danach nicht mehr.',
                )
              ) {
                regenerate.mutate();
              }
            }}
          >
            Token neu erzeugen
          </Button>
        </div>
      )}
    </Card>
  );
}

/** "Mit PayPal bezahlen" auf einer Ball-Produktkarte (§ Nutzeranfrage) — für
 * jedes Mitglied sichtbar. Ein Klick legt eine Kaufabsicht an (erst
 * `pending`, siehe docs/equipment.md) und öffnet direkt den PayPal.me-Link in
 * einem neuen Tab. Da PayPal.me keinen vorbefüllten Verwendungszweck
 * unterstützt, zeigt die Karte den Vorschlag zusätzlich zum Kopieren an —
 * das Mitglied fügt ihn im PayPal-Zahlungsfenster selbst ein. */
function BuyPaypalButton({ item }: { item: EquipmentItemResponse }) {
  const [note, setNote] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buy = useMutation({
    mutationFn: () =>
      api<CreatePurchaseIntentResponse>(`/equipment/${item.id}/paypal-purchase-intent`, {
        method: 'POST',
        orgScoped: true,
      }),
  });

  // Das Tab MUSS synchron im Klick-Handler geöffnet werden, nicht erst in
  // `onSuccess` der Mutation: Browser werten einen window.open()-Aufruf nach
  // einem await (hier die API-Antwort) nicht mehr als direkte Folge der
  // Nutzerinteraktion und blockieren ihn als Popup — ohne jede Fehlermeldung,
  // der Klick wirkte dann einfach wirkungslos.
  const handleClick = () => {
    const tab = window.open('', '_blank');
    buy.mutate(undefined, {
      onSuccess: (res) => {
        setError(null);
        setNote(res.suggestedNote);
        setRedirectUrl(res.redirectUrl);
        if (tab) tab.location.href = res.redirectUrl;
      },
      onError: (e) => {
        tab?.close();
        setError(e instanceof ApiError ? e.message : 'Konnte nicht zu PayPal weiterleiten.');
      },
    });
  };

  return (
    <div className="mt-2">
      <Button
        type="button"
        className="gap-1.5 text-xs"
        disabled={buy.isPending}
        onClick={handleClick}
      >
        <CreditCard size={14} /> Mit PayPal bezahlen
      </Button>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      {redirectUrl && (
        <p className="mt-1 text-xs text-slate-500">
          Falls sich kein neues Fenster geöffnet hat:{' '}
          <a
            href={redirectUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-blue-600 underline"
          >
            Zu PayPal
          </a>
        </p>
      )}
      {note && (
        <div className="mt-1.5 flex items-center gap-1.5 text-xs text-slate-500">
          <span>
            Bitte als Verwendungszweck bei PayPal einfügen:{' '}
            <span className="font-medium text-slate-700">{note}</span>
          </span>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(note);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Verwendungszweck kopieren"
          >
            {copied ? <Check size={13} /> : <Copy size={13} />}
          </button>
        </div>
      )}
    </div>
  );
}

const PURCHASE_STATUS_LABELS: Record<EquipmentPurchaseResponse['status'], string> = {
  pending: 'Ausstehend',
  confirmed: 'Bestätigt',
  cancelled: 'Storniert',
};

/** Verkaufsliste eines Ball-Produkts (§ Nutzeranfrage „auswerten können,
 * welches Mitglied welchen Ball wie oft gekauft hat") — nur `equipment.manage`.
 * Zeigt jede Eintragung (auch ausstehende, zur Transparenz) plus eine
 * Zusammenfassung je Mitglied, die ausschließlich bestätigte Käufe zählt
 * (§ Nutzeranfrage „erst registrieren, wenn die Zahlung erfolgreich
 * durchgeführt worden ist"). Jede Zeile ist per Status-Dropdown korrigierbar
 * — die Betrags-Zuordnung einer PayPal-Bestätigung ist eine Heuristik und
 * kann in Randfällen danebenliegen. */
function PurchaseListModal({
  item,
  onClose,
}: {
  item: EquipmentItemResponse;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data: purchases } = useQuery({
    queryKey: ['equipment', 'purchases', item.id],
    queryFn: () =>
      api<EquipmentPurchaseResponse[]>(`/equipment/purchases?equipmentItemId=${item.id}`, {
        orgScoped: true,
      }),
  });

  const correct = useMutation({
    mutationFn: ({ id, status }: { id: string; status: EquipmentPurchaseResponse['status'] }) =>
      api(`/equipment/purchases/${id}`, { method: 'PATCH', orgScoped: true, body: { status } }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['equipment', 'purchases', item.id] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Korrektur fehlgeschlagen.'),
  });

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => api(`/equipment/purchases/${id}`, { method: 'DELETE', orgScoped: true }),
    onMutate: (id) => setDeletingId(id),
    onSettled: () => setDeletingId(null),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['equipment', 'purchases', item.id] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Löschen fehlgeschlagen.'),
  });

  const confirmedCountByMember = new Map<string, { name: string; count: number }>();
  for (const p of purchases ?? []) {
    if (p.status !== 'confirmed') continue;
    const key = p.memberId ?? p.memberName;
    const entry = confirmedCountByMember.get(key) ?? { name: p.memberName, count: 0 };
    entry.count += 1;
    confirmedCountByMember.set(key, entry);
  }
  const summary = [...confirmedCountByMember.values()].sort((a, b) => b.count - a.count);

  return (
    <Modal title={`Verkaufsliste — ${item.name}`} icon={<ListChecks size={18} />} onClose={onClose}>
      <div className="space-y-4">
        {error && <Alert>{error}</Alert>}

        {summary.length > 0 && (
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Bestätigte Käufe je Mitglied
            </p>
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {summary.map((s) => (
                <li key={s.name} className="flex items-center justify-between px-3 py-1.5 text-sm">
                  <span className="text-slate-700">{s.name}</span>
                  <span className="font-medium text-slate-500">{s.count}×</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Alle Eintragungen
          </p>
          {purchases && purchases.length > 0 ? (
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {purchases.map((p) => (
                <li
                  key={p.id}
                  className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-slate-700">{p.memberName}</p>
                    <p className="text-xs text-slate-400">
                      {new Date(p.clickedAt).toLocaleString('de-DE', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      })}
                      {' · '}
                      {(p.priceCents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <select
                      value={p.status}
                      disabled={correct.isPending}
                      onChange={(e) =>
                        correct.mutate({
                          id: p.id,
                          status: e.target.value as EquipmentPurchaseResponse['status'],
                        })
                      }
                      className={`rounded-md border px-2 py-1 text-xs font-medium ${
                        p.status === 'confirmed'
                          ? 'border-green-200 bg-green-50 text-green-700'
                          : p.status === 'cancelled'
                            ? 'border-slate-200 bg-slate-100 text-slate-500'
                            : 'border-amber-200 bg-amber-50 text-amber-700'
                      }`}
                    >
                      {(['pending', 'confirmed', 'cancelled'] as const).map((s) => (
                        <option key={s} value={s}>
                          {PURCHASE_STATUS_LABELS[s]}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      title="Eintragung löschen"
                      disabled={deletingId === p.id}
                      onClick={() => {
                        if (confirm(`Eintragung von ${p.memberName} wirklich löschen?`)) {
                          remove.mutate(p.id);
                        }
                      }}
                      className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">Noch keine Kaufversuche.</p>
          )}
        </div>

        <div className="flex justify-end pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Schließen
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Verfügbarkeits-Dropdown für die Rubrik „Schuhe" (§ Nutzeranfrage) — ersetzt
 * dort die Zeile aus einzelnen Größen-Kapseln (bleibt für die übrigen
 * Rubriken unverändert, siehe `CategorySection`), da eine Liste aus über
 * einem Dutzend EU-Schuhgrößen als Kapsel-Reihe schnell unübersichtlich
 * wird. Rein anzeigend/merkend (kein „in den Warenkorb" dahinter — Schuhe
 * gehören nicht zu `SELLABLE_EQUIPMENT_CATEGORIES`, bleiben Kommissionsware
 * zum Testen) — eine Auswahl merkt sich nur, welche Größe man sich gerade
 * ansieht. Eigene, einfache Popover-Liste statt eines nativen `<select>`:
 * ein natives `<option>` lässt sich nicht mit Punkt-Icon + rechtsbündigem
 * Verfügbarkeits-Text + Hover-Rahmen gestalten, wie von der Rubrik gefordert. */
function ShoeSizeDropdown({ sizes }: { sizes: EquipmentItemSize[] }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<EquipmentItemSize | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside(rootRef, () => setOpen(false), open);

  return (
    <div className="relative mt-1.5 max-w-xs" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border-2 border-brand-accent px-3 py-2 text-left text-sm"
      >
        <span className={selected ? 'font-medium text-slate-800' : 'text-slate-400'}>
          {selected ? `Größe ${selected.size}` : 'Größe auswählen'}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white p-1.5 shadow-lg">
          {sizes.map((s) => {
            const available = s.quantity > 0;
            const isSelected = selected?.size === s.size;
            return (
              <button
                key={s.size}
                type="button"
                onClick={() => {
                  setSelected(s);
                  setOpen(false);
                }}
                className={`flex w-full items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-sm ${
                  available
                    ? 'border-green-100 bg-green-50 hover:border-green-300'
                    : 'border-red-100 bg-red-50 hover:border-red-300'
                } ${isSelected ? (available ? 'border-green-400' : 'border-red-400') : ''}`}
              >
                <span className="flex items-center gap-2">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${available ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="font-medium text-slate-800">Größe {s.size}</span>
                </span>
                <span className={available ? 'text-green-700' : 'text-red-600'}>
                  {available ? 'verfügbar' : 'nicht verfügbar'}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CategorySection({
  items,
  canManage,
  onAdd,
  onEdit,
  onDelete,
  onManageImages,
  onOpenGallery,
  onViewPurchases,
  deletingId,
}: {
  items: EquipmentItemResponse[];
  canManage: boolean;
  onAdd: () => void;
  onEdit: (item: EquipmentItemResponse) => void;
  onDelete: (item: EquipmentItemResponse) => void;
  onManageImages: (item: EquipmentItemResponse) => void;
  onOpenGallery: (item: EquipmentItemResponse) => void;
  onViewPurchases: (item: EquipmentItemResponse) => void;
  deletingId: string | null;
}) {
  return (
    <Card>
      {items.length > 0 ? (
        <ul className="mb-4 divide-y divide-slate-100">
          {items.map((item) => {
            const cover = item.images.find((i) => i.id === item.coverImageId) ?? item.images[0];
            return (
              <li key={item.id} className="py-3">
                <div className="flex items-start gap-3">
                  {cover ? (
                    <button
                      type="button"
                      onClick={() => onOpenGallery(item)}
                      className="shrink-0"
                      aria-label={`Bilder von ${item.name} ansehen`}
                    >
                      <img
                        src={`/api/org/files/${cover.fileId}`}
                        alt={item.name}
                        className="h-24 w-24 rounded-md border border-slate-200 object-cover"
                      />
                    </button>
                  ) : (
                    <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-md border border-dashed border-slate-300 text-slate-300">
                      <ImageIcon size={28} />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800">{item.name}</p>
                    <p className="mt-0.5 text-sm font-semibold text-brand-accent">
                      {formatPrice(item.priceCents)}
                    </p>
                    {item.description && (
                      <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">
                        {item.description}
                      </p>
                    )}
                    {item.sizes.length > 0 && item.category === 'shoes' && (
                      <ShoeSizeDropdown sizes={item.sizes} />
                    )}
                    {item.sizes.length > 0 && item.category !== 'shoes' && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {item.sizes.map((s) => (
                          <span
                            key={s.size}
                            className={`rounded-full border px-2 py-0.5 text-xs font-medium ${
                              s.quantity > 0
                                ? 'border-slate-200 bg-slate-50 text-slate-600'
                                : 'border-red-100 bg-red-50 text-red-500'
                            }`}
                          >
                            {s.size}: {s.quantity > 0 ? s.quantity : 'ausverkauft'}
                          </span>
                        ))}
                      </div>
                    )}
                    {item.images.length > 1 && (
                      <button
                        type="button"
                        onClick={() => onOpenGallery(item)}
                        className="mt-1 text-xs text-brand-accent hover:underline"
                      >
                        {item.images.length} Bilder ansehen
                      </button>
                    )}
                    {SELLABLE_EQUIPMENT_CATEGORIES.includes(item.category) && (
                      <BuyPaypalButton item={item} />
                    )}
                  </div>
                  {canManage && (
                    <div className="flex shrink-0 gap-1">
                      {SELLABLE_EQUIPMENT_CATEGORIES.includes(item.category) && (
                        <Button
                          variant="ghost"
                          className="px-2 py-1 text-xs"
                          onClick={() => onViewPurchases(item)}
                          aria-label="Verkaufsliste ansehen"
                          title="Verkaufsliste"
                        >
                          <ListChecks size={14} />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs"
                        onClick={() => onManageImages(item)}
                        aria-label="Bilder verwalten"
                      >
                        <ImagePlus size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs"
                        onClick={() => onEdit(item)}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        className="px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                        disabled={deletingId === item.id}
                        onClick={() => onDelete(item)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mb-4 text-sm text-slate-500">Noch keine Angebote in dieser Rubrik.</p>
      )}
      {canManage && (
        <Button variant="ghost" className="text-xs" onClick={onAdd}>
          <Plus size={14} className="mr-1" /> Produkt hinzufügen
        </Button>
      )}
    </Card>
  );
}

export function EquipmentPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const canManage = can('equipment.manage');
  const [activeCategory, setActiveCategory] = useState<EquipmentCategory>('balls');
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<{
    category: EquipmentCategory;
    item: EquipmentItemResponse | null;
  } | null>(null);
  const [imagesFor, setImagesFor] = useState<string | null>(null);
  const [gallery, setGallery] = useState<{ itemId: string; index: number } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [purchasesFor, setPurchasesFor] = useState<string | null>(null);

  const { data: items } = useQuery({
    queryKey: ['equipment'],
    queryFn: () => api<EquipmentItemResponse[]>('/equipment', { orgScoped: true }),
  });

  const create = useMutation({
    mutationFn: (form: FormState) =>
      api('/equipment', {
        method: 'POST',
        orgScoped: true,
        body: {
          category: form.category,
          name: form.name.trim(),
          description: form.description.trim() || null,
          sizes: cleanSizes(form.sizes),
          priceCents: parsePriceInput(form.price) ?? 0,
        },
      }),
    onSuccess: () => {
      setDialog(null);
      void qc.invalidateQueries({ queryKey: ['equipment'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Anlegen fehlgeschlagen.'),
  });

  const update = useMutation({
    mutationFn: ({ id, form }: { id: string; form: FormState }) =>
      api(`/equipment/${id}`, {
        method: 'PATCH',
        orgScoped: true,
        body: {
          category: form.category,
          name: form.name.trim(),
          description: form.description.trim() || null,
          sizes: cleanSizes(form.sizes),
          priceCents: parsePriceInput(form.price) ?? 0,
        },
      }),
    onSuccess: () => {
      setDialog(null);
      void qc.invalidateQueries({ queryKey: ['equipment'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/equipment/${id}`, { method: 'DELETE', orgScoped: true }),
    onMutate: (id) => setDeletingId(id),
    onSettled: () => setDeletingId(null),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['equipment'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Löschen fehlgeschlagen.'),
  });

  const itemsByCategory = (category: EquipmentCategory) =>
    (items ?? []).filter((i) => i.category === category);
  const counts = Object.fromEntries(
    EQUIPMENT_CATEGORIES.map((c) => [c, itemsByCategory(c).length]),
  ) as Record<EquipmentCategory, number>;

  // Von der `items`-Query abgeleitet statt beim Öffnen als Schnappschuss
  // gespeichert, damit ein Upload/Löschen im jeweiligen Dialog sofort
  // sichtbar wird, sobald die Query danach neu lädt.
  const imagesDialogItem = items?.find((i) => i.id === imagesFor) ?? null;
  const galleryItem = items?.find((i) => i.id === gallery?.itemId) ?? null;
  const purchaseListItem = items?.find((i) => i.id === purchasesFor) ?? null;
  const isSellableCategory = SELLABLE_EQUIPMENT_CATEGORIES.includes(activeCategory);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="hidden text-xl font-semibold md:block">Equipment</h1>
        <p className="mt-1 text-sm text-slate-500">
          {isSellableCategory
            ? 'Produkte, die Mitglieder direkt über PayPal beim Verein kaufen können.'
            : 'Kommissionsware, die über den Verein zum Testen zur Verfügung steht — unverbindlich, ohne direkten Kauf über die App.'}
        </p>
      </div>

      <CategoryTabs active={activeCategory} onChange={setActiveCategory} counts={counts} />

      {error && <Alert>{error}</Alert>}

      {isSellableCategory && canManage && <PaypalSettingsCard />}

      <CategorySection
        items={itemsByCategory(activeCategory)}
        canManage={canManage}
        onAdd={() => {
          setError(null);
          setDialog({ category: activeCategory, item: null });
        }}
        onEdit={(item) => {
          setError(null);
          setDialog({ category: item.category, item });
        }}
        onDelete={(item) => {
          if (confirm(`„${item.name}" wirklich löschen?`)) remove.mutate(item.id);
        }}
        onManageImages={(item) => setImagesFor(item.id)}
        onOpenGallery={(item) => {
          const coverIndex = item.images.findIndex((i) => i.id === item.coverImageId);
          setGallery({ itemId: item.id, index: coverIndex >= 0 ? coverIndex : 0 });
        }}
        onViewPurchases={(item) => setPurchasesFor(item.id)}
        deletingId={deletingId}
      />

      {purchaseListItem && (
        <PurchaseListModal item={purchaseListItem} onClose={() => setPurchasesFor(null)} />
      )}

      {dialog && (
        <ItemDialog
          initial={
            dialog.item
              ? {
                  category: dialog.item.category,
                  name: dialog.item.name,
                  description: dialog.item.description ?? '',
                  sizes: dialog.item.sizes,
                  price: centsToPriceInput(dialog.item.priceCents),
                }
              : { ...EMPTY_FORM, category: dialog.category }
          }
          onClose={() => setDialog(null)}
          submitting={create.isPending || update.isPending}
          error={null}
          onSubmit={(form) => {
            if (dialog.item) update.mutate({ id: dialog.item.id, form });
            else create.mutate(form);
          }}
        />
      )}

      {imagesDialogItem && (
        <ManageImagesDialog item={imagesDialogItem} onClose={() => setImagesFor(null)} />
      )}

      {galleryItem && gallery && (
        <Lightbox
          images={galleryItem.images}
          initialIndex={gallery.index}
          title={galleryItem.name}
          onClose={() => setGallery(null)}
        />
      )}
    </div>
  );
}
